import React, { useEffect, useState } from "react";
import { getS3SignedUrl } from "../../utils/s3Client";
import type { FireDrill } from "../../types/api";
import { useOrgUsersStore } from "../../store/store";
import DocumentViewer from "../../components/DocumentViewer/DocumentViewer";
import PdfViewer from "../../components/pdf/PdfViewer";
import {
  XIcon,
  Flame,
  MapPin,
  Building,
  Calendar,
  User,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Mail,
  FileText,
  Paperclip,
  ExternalLink,
  Timer
} from "lucide-react";

interface ViewFireDrillProps {
  fireDrill: FireDrill;
  onClose: () => void;
}

interface OrgUser {
  _id: { $oid: string };
  firstName: string;
  lastName: string;
  employeeId: string;
  email: string;
  mobile: string;
  premise?: string | Array<{ $oid: string }> | string[];
}

interface EvidenceFile {
  name?: string;
  url?: string;
}

// Reusable Sub-components for modern card UI layout
const ViewCard = ({
  title,
  icon: Icon,
  children,
  className = "",
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`bg-white border border-slate-100 rounded-2xl p-5 shadow-sm space-y-4 ${className}`}>
    <div className="flex items-center gap-2 pb-3 border-b border-slate-50">
      <Icon className="w-4 h-4 text-slate-500" />
      <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">{title}</h3>
    </div>
    {children}
  </div>
);

const InfoField = ({
  icon: Icon,
  label,
  value,
}: {
  icon: any;
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-slate-50/50 transition-colors duration-150">
    <div className="p-2 bg-slate-50 text-slate-500 border border-slate-100/50 rounded-lg mt-0.5">
      <Icon className="w-3.5 h-3.5" />
    </div>
    <div className="space-y-0.5">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">{label}</span>
      <div className="text-sm font-semibold text-slate-800 block leading-tight">
        {value || <span className="text-slate-400 font-medium">-</span>}
      </div>
    </div>
  </div>
);

const TextBox = ({
  label,
  value,
  type = "info",
}: {
  label: string;
  value?: string;
  type?: "info" | "warning" | "success";
}) => {
  let borderClass = "border-slate-100 bg-slate-50/30";
  let textClass = "text-slate-700";
  
  if (type === "warning") {
    borderClass = "border-amber-100 bg-amber-50/10";
    textClass = "text-amber-900";
  } else if (type === "success") {
    borderClass = "border-emerald-100 bg-emerald-50/10";
    textClass = "text-emerald-900";
  }

  return (
    <div className={`p-4 rounded-xl border ${borderClass} space-y-2`}>
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">{label}</span>
      </div>
      <p className={`text-sm leading-relaxed ${value ? textClass : 'text-slate-400 italic'}`}>
        {value || "No records or entries provided."}
      </p>
    </div>
  );
};

const ViewFireDrill: React.FC<ViewFireDrillProps> = ({
  fireDrill,
  onClose,
}) => {
  const [fileUrls, setFileUrls] = useState<
    { name: string; url: string; type: string }[]
  >([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [fileurl, setFileUrl] = useState<{
    url: string;
    name: string;
    type: string;
  }>({
    url: "",
    name: "",
    type: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const { orgUsersData } = useOrgUsersStore();

  const formatTime = (timeString: string | null | undefined): string => {
    if (!timeString) return "";
    try {
      const [hour, minutes] = timeString.split(":");
      const hourInt = parseInt(hour, 10);
      const period = hourInt >= 12 ? "PM" : "AM";
      const twelveHour = hourInt % 12 || 12;
      return `${twelveHour}:${minutes} ${period}`;
    } catch (e) {
      return timeString;
    }
  };

  const getResponsiblePersonName = (id: string): string => {
    if (!id) return "Not assigned";
    const user = orgUsersData.find((user: OrgUser) => {
      const userId =
        typeof user._id === "object" && "$oid" in user._id
          ? user._id.$oid
          : String(user._id);
      return userId === id;
    });
    return user ? `${user.firstName} ${user.lastName}` : "Not available";
  };

  const getMimeType = (extension: string): string => {
    switch (extension.toLowerCase()) {
      case "pdf":
        return "application/pdf";
      case "doc":
      case "docx":
        return "application/msword";
      case "jpg":
      case "jpeg":
        return "image/jpeg";
      case "png":
        return "image/png";
      case "txt":
        return "text/plain";
      default:
        return "application/octet-stream";
    }
  };

  const viewFile = async (
    fileKey: string,
    fileType: string,
    displayName: string
  ) => {
    try {
      setIsLoading(true);
      const fileurl = await getS3SignedUrl(fileKey, getMimeType(fileType));
      const filobj = {
        url: fileurl,
        name: displayName,
        type: fileType,
      };
      setFileUrl(filobj);
    } catch (err: any) {
      setErrorMessage("File not found");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchFiles = async () => {
      if (!fireDrill?.evidence_closure_files) {
        setFileUrls([]);
        return;
      }

      try {
        const files = await Promise.all(
          (fireDrill.evidence_closure_files ?? []).map(
            async (file: string | EvidenceFile) => {
              let filePath = "";
              if (typeof file === "string") {
                filePath = file;
              } else if (
                typeof file === "object" &&
                "url" in file &&
                file.url
              ) {
                filePath = file.url;
              } else if (
                typeof file === "object" &&
                "name" in file &&
                file.name
              ) {
                filePath = file.name;
              }

              const fileName = filePath.split("/").pop() || "";
              const fileType = fileName.split(".").pop()?.toLowerCase() || "";
              return {
                name: fileName,
                url: filePath,
                type: fileType,
              };
            }
          )
        );
        setFileUrls(files);
        setErrorMessage("");
      } catch (error) {
        setErrorMessage(
          `Failed to load attachments: ${
            error instanceof Error ? error.message : "Unknown error"
          }`
        );
        setFileUrls([]);
      }
    };

    fetchFiles();
  }, [fireDrill]);

  const responsiblePersonName = getResponsiblePersonName(
    fireDrill.person_responsibility
  );

  const renderParticipationRate = (rate: string | number | undefined | null) => {
    if (rate === undefined || rate === null || rate === "") {
      return <span className="text-slate-400 font-medium">-</span>;
    }
    const numRate = Number(rate);
    if (isNaN(numRate)) return <span className="text-slate-800 font-semibold">{rate}</span>;

    let colorClass = "bg-red-50 text-red-700 border-red-100";
    if (numRate >= 80) colorClass = "bg-emerald-50 text-emerald-700 border-emerald-100";
    else if (numRate >= 50) colorClass = "bg-amber-50 text-amber-700 border-amber-100";

    return (
      <div className="flex items-center gap-2 mt-0.5">
        <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${colorClass}`}>
          {numRate}%
        </span>
        <div className="w-12 h-1.5 bg-slate-100 rounded-full overflow-hidden hidden sm:block">
          <div 
            className={`h-full rounded-full ${numRate >= 80 ? 'bg-emerald-500' : numRate >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} 
            style={{ width: `${numRate}%` }}
          />
        </div>
      </div>
    );
  };

  const renderDuration = (val: string | number | undefined | null) => {
    if (!val) return <span className="text-slate-400 font-medium">-</span>;
    return (
      <span className="inline-flex items-center gap-1 text-slate-800 font-semibold mt-0.5">
        {val} <span className="text-xs font-normal text-slate-500">mins</span>
      </span>
    );
  };

  return (
    <div className="flex flex-col h-[90vh] bg-slate-50/30">
      {/* Redesigned Modal Header */}
      <div className="flex-none px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-red-50 text-red-600 rounded-xl border border-red-100/50">
            <Flame className="w-5 h-5 animate-pulse" />
          </div>
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Fire Operations</span>
            <h2 className="text-lg font-bold text-slate-800 tracking-tight">View Fire Drill</h2>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-slate-100 text-slate-400 hover:text-slate-600 rounded-lg transition-colors duration-200"
          aria-label="Close modal"
        >
          <XIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Modal Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {errorMessage && (
          <div className="p-3 bg-red-50 border border-red-100 text-red-700 text-sm rounded-xl flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Card 1: Location & Ownership */}
          <ViewCard title="Location & Responsibility" icon={MapPin}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoField icon={MapPin} label="Region" value={fireDrill.region} />
              <InfoField icon={Building} label="Premise" value={fireDrill.premise} />
              <InfoField icon={Calendar} label="Date of test/drill" value={fireDrill.date_of_test_drills} />
              <InfoField icon={User} label="Responsible Person" value={responsiblePersonName} />
            </div>
          </ViewCard>

          {/* Card 2: Timing & Performance Metrics */}
          <ViewCard title="Timelines & Efficiency" icon={Timer}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoField icon={Clock} label="Timeline to close" value={formatTime(fireDrill.closing_timeline)} />
              <InfoField icon={Calendar} label="Date of evidence" value={fireDrill.date_of_evidence} />
              <InfoField icon={CheckCircle2} label="Participation Rate" value={renderParticipationRate(fireDrill.participation_rate)} />
              <InfoField icon={Timer} label="Average Drill Duration" value={renderDuration(fireDrill.average_drill)} />
              <InfoField icon={TrendingUp} label="Evacuation Time Trend" value={renderDuration(fireDrill.evacuation_trend)} />
              <InfoField icon={Calendar} label="Next Drill Scheduled" value={fireDrill.next_drill_scheduled} />
            </div>
          </ViewCard>

          {/* Card 3: Observations & Actions (Full width) */}
          <ViewCard title="Observations & Action Plan" icon={AlertCircle} className="lg:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextBox
                label="Observation Identified for Improvement"
                value={fireDrill.observation_identified_for_improvement}
                type="warning"
              />
              <TextBox
                label="Action to be taken"
                value={fireDrill.actions}
                type="info"
              />
              <TextBox
                label="Evidence of closure"
                value={fireDrill.closure_evidence}
                type="success"
              />
              {fireDrill.moduleType === "external_emergency_preparedness" && (
                <TextBox
                  label="Feedback & Gap Analysis"
                  value={fireDrill.feedback_gap_analysis}
                  type="info"
                />
              )}
            </div>
          </ViewCard>

          {/* Card 4: Attachments & Report Sharing (Full width) */}
          <ViewCard title="Attachments & Distribution" icon={Paperclip} className="lg:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Evidence Attachments */}
              <div className="space-y-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                  Evidence Attachments
                </span>
                {fileUrls.length > 0 ? (
                  <div className="grid grid-cols-1 gap-2">
                    {fileUrls.map((file, index) => {
                      const isPdf = file.type === "pdf";
                      const isImg = ["jpg", "jpeg", "png"].includes(file.type);
                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100/80 border border-slate-100/50 rounded-xl transition-all duration-150 group"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className={`p-2 rounded-lg ${
                                isPdf
                                  ? "bg-red-50 text-red-500"
                                  : isImg
                                  ? "bg-blue-50 text-blue-500"
                                  : "bg-slate-100 text-slate-600"
                              }`}
                            >
                              <FileText className="w-4 h-4" />
                            </div>
                            <div className="min-w-0">
                              <p
                                className="text-xs font-semibold text-slate-700 truncate max-w-[240px]"
                                title={file.name}
                              >
                                {file.name}
                              </p>
                              <p className="text-[9px] uppercase font-bold text-slate-400">
                                {file.type || "FILE"}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              viewFile(file.url, file.type, file.name)
                            }
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 bg-white hover:bg-blue-50 border border-slate-100 rounded-lg shadow-sm hover:shadow-md transition-all duration-150"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            View
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-6 bg-slate-50/50 border border-dashed border-slate-200 rounded-xl text-center">
                    <Paperclip className="w-5 h-5 text-slate-300 mb-1" />
                    <p className="text-xs text-slate-400">No evidence files attached</p>
                  </div>
                )}
              </div>

              {/* Report Recipients */}
              <div className="space-y-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                  Report Recipients
                </span>
                {fireDrill.report_send_email?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {fireDrill.report_send_email.map((email, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1.5 bg-blue-50/50 hover:bg-blue-50 text-blue-700 border border-blue-100/50 px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-150"
                      >
                        <Mail className="w-3.5 h-3.5 text-blue-500" />
                        {email}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-6 bg-slate-50/50 border border-dashed border-slate-200 rounded-xl text-center">
                    <Mail className="w-5 h-5 text-slate-300 mb-1" />
                    <p className="text-xs text-slate-400">No report recipients specified</p>
                  </div>
                )}
              </div>
            </div>
          </ViewCard>
        </div>
      </div>

      {/* Redesigned Document Viewer Modal */}
      {fileurl.url !== "" && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="relative w-full max-w-5xl h-[85vh] bg-white border border-slate-100 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
            {/* Header of Viewer */}
            <div className="flex-none px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                <span className="text-sm font-semibold text-slate-800 truncate max-w-md">
                  {fileurl.name}
                </span>
              </div>
              <button
                onClick={() => setFileUrl({ url: "", name: "", type: "" })}
                className="p-1.5 hover:bg-slate-100 text-slate-400 hover:text-slate-600 rounded-lg transition-colors duration-200"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Body of Viewer */}
            <div className="flex-1 overflow-auto p-4 bg-slate-100/30">
              {isLoading ? (
                <div className="flex flex-col justify-center items-center h-full gap-3">
                  <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-blue-500"></div>
                  <span className="text-xs text-slate-500 font-medium">Loading document...</span>
                </div>
              ) : fileurl.type === "pdf" ? (
                <PdfViewer fileUrl={fileurl.url} />
              ) : (
                <DocumentViewer file={fileurl.url} name={fileurl.name} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Redesigned Modal Footer */}
      <div className="flex-none py-4 px-6 bg-slate-50/80 border-t border-slate-100 flex justify-end gap-3 rounded-b-xl">
        <button
          type="button"
          className="bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 hover:text-slate-800 font-semibold px-6 py-2.5 rounded-xl shadow-sm hover:shadow transition-all duration-200 text-xs uppercase tracking-wider"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default ViewFireDrill;
